// A dock is a region that hosts one or more apparatus panels and keeps exactly
// one of them visible at a time. Sites lay out as many docks as they have
// distinct places to put things -- the Tosefta site runs a bottom dock (wide
// things: synopsis tables, manuscript folios) and a side dock (tall things:
// commentaries, whose paragraphs need vertical room and would wreck the
// reading column if inlined).
//
// The tab strip appears only when a dock is hosting more than one enabled
// layer. That rule matters: tabs hide things, and hiding is exactly wrong for
// an apparatus the reader wants beside the text. So we never pay for a tab
// strip until two layers are actually competing for the same region.
//
//   layer.mount(container, ctx) -> instance
//       instance.select(addr, meta)   position the panel (optional)
//       instance.setContext(ctx)      a new work loaded (optional)
//       instance.refresh()            re-render in place (optional)
//       instance.destroy()            release listeners/DOM (optional)
//       instance.headerControls       node(s) to hoist into the dock header
//
// Instances are mounted once and outlive a work change: a panel keeps its
// scroll position and internal state when an unrelated layer is toggled. That
// means the *data* has to reach them by a separate route, hence setContext --
// mount-time ctx is only the first of many.

export const DOCK_LABELS = {
    close: 'סגור',
    empty: 'לא נבחרה תצוגה',
};

export function createDock(options) {
    const {
        root,
        onVisibilityChange = null,
        onActiveChange = null,
        // Host-supplied controls that belong to the DOCK rather than to any one
        // layer, so they persist across tab switches (the commentary dock's pin
        // toggle: which layer is showing doesn't change whether the dock
        // follows the reader's scroll position).
        headerControls = null,
    } = options;

    if (!root) throw new Error('createDock: `root` is required');
    const labels = { ...DOCK_LABELS, ...(options.labels || {}) };

    root.classList.add('mvc-dock');
    root.innerHTML = '';

    const header = el('div', 'mvc-dock-header');
    const tabs = el('div', 'mvc-dock-tabs');
    const titleEl = el('span', 'mvc-dock-title');
    const extras = el('div', 'mvc-dock-extras');
    const fixedExtras = el('div', 'mvc-dock-fixed-extras');
    if (headerControls) {
        (Array.isArray(headerControls) ? headerControls : [headerControls])
            .forEach(n => fixedExtras.appendChild(n));
    }
    const closeBtn = el('button', 'mvc-dock-close');
    closeBtn.type = 'button';
    closeBtn.textContent = '✕';
    closeBtn.title = labels.close;
    closeBtn.addEventListener('click', () => hide());
    header.append(tabs, titleEl, extras, fixedExtras, closeBtn);

    const body = el('div', 'mvc-dock-body');
    root.append(header, body);

    // id -> { layer, container, instance, stale }
    const hosted = new Map();
    let order = [];
    let activeId = null;
    let lastAddr = null;
    let lastMeta = {};
    let lastCtx = undefined;

    function renderTabs() {
        tabs.innerHTML = '';
        const multi = order.length > 1;
        tabs.hidden = !multi;
        titleEl.hidden = multi;
        if (!multi) {
            const only = order.length ? hosted.get(order[0]) : null;
            titleEl.textContent = only ? (only.layer.label || only.layer.id) : labels.empty;
            return;
        }
        order.forEach(id => {
            const entry = hosted.get(id);
            const btn = el('button', 'mvc-dock-tab' + (id === activeId ? ' active' : ''));
            btn.type = 'button';
            btn.textContent = entry.layer.label || id;
            if (entry.layer.title) btn.title = entry.layer.title;
            btn.setAttribute('aria-pressed', String(id === activeId));
            btn.addEventListener('click', () => activate(id));
            tabs.appendChild(btn);
        });
    }

    // Only the visible layer is kept in sync; the others are marked stale and
    // catch up when their tab is opened. A commentary panel re-rendering a few
    // hundred notes for a tab nobody is looking at is pure waste, and on the
    // OCR'd commentaries that render is not cheap.
    function pushSelection(entry) {
        if (!entry || !entry.instance || typeof entry.instance.select !== 'function') return;
        try {
            entry.instance.select(lastAddr, lastMeta);
            entry.stale = false;
        } catch (err) {
            console.error(`[mvc] dock layer "${entry.layer.id}" failed to select`, err);
        }
    }

    function activate(id) {
        if (!hosted.has(id)) return;
        activeId = id;
        hosted.forEach((entry, key) => {
            entry.container.hidden = key !== id;
        });
        const entry = hosted.get(id);
        renderExtras(entry);
        if (entry.stale) pushSelection(entry);
        renderTabs();
        if (onActiveChange) onActiveChange(id);
    }

    // A layer may contribute its own header controls (the synopsis panel's
    // prev/next halakhah buttons, a "whole chapter" toggle). They live in the
    // dock header rather than inside the layer's body so they stay put when
    // the body scrolls.
    function renderExtras(entry) {
        extras.innerHTML = '';
        const nodes = entry && entry.instance && entry.instance.headerControls;
        if (!nodes) return;
        (Array.isArray(nodes) ? nodes : [nodes]).forEach(n => extras.appendChild(n));
    }

    function show() {
        if (!root.hidden) return;
        root.hidden = false;
        if (onVisibilityChange) onVisibilityChange(true);
        // Whatever arrived while hidden was not applied to the active layer.
        const entry = hosted.get(activeId);
        if (entry && entry.stale) pushSelection(entry);
    }

    function hide() {
        if (root.hidden) return;
        root.hidden = true;
        if (onVisibilityChange) onVisibilityChange(false);
    }

    return {
        get root() { return root; },
        get activeId() { return activeId; },
        get layerIds() { return [...order]; },
        get visible() { return !root.hidden; },

        // Reconcile the dock to exactly this list of layers, in this order.
        // Layers already hosted keep their mounted instance and scroll state --
        // toggling an unrelated layer must not blow away the panel the reader
        // is reading.
        setLayers(layers, ctx) {
            const ctxChanged = ctx !== lastCtx;
            lastCtx = ctx;
            // A new work invalidates whatever every panel is showing, including
            // the position: last tractate's baseIdx means nothing in this one.
            if (ctxChanged) { lastAddr = null; lastMeta = {}; }

            const wanted = new Set(layers.map(l => l.id));
            [...hosted.keys()].forEach(id => {
                if (wanted.has(id)) return;
                const entry = hosted.get(id);
                if (entry.instance && typeof entry.instance.destroy === 'function') {
                    try { entry.instance.destroy(); } catch (err) {
                        console.error(`[mvc] dock layer "${id}" failed to destroy`, err);
                    }
                }
                entry.container.remove();
                hosted.delete(id);
            });

            layers.forEach(layer => {
                if (hosted.has(layer.id)) {
                    const entry = hosted.get(layer.id);
                    entry.layer = layer;
                    if (ctxChanged) {
                        entry.stale = true;
                        if (entry.instance && typeof entry.instance.setContext === 'function') {
                            try { entry.instance.setContext(ctx); } catch (err) {
                                console.error(`[mvc] dock layer "${layer.id}" failed setContext`, err);
                            }
                        }
                    }
                    return;
                }
                const container = el('div', 'mvc-dock-pane');
                container.dataset.layerId = layer.id;
                container.hidden = true;
                body.appendChild(container);
                let instance = null;
                try {
                    instance = layer.mount(container, ctx) || null;
                } catch (err) {
                    console.error(`[mvc] dock layer "${layer.id}" failed to mount`, err);
                    container.textContent = '';
                }
                hosted.set(layer.id, { layer, container, instance, stale: true });
            });

            // Keep the DOM in the declared order so tab order matches.
            order = layers.map(l => l.id);
            order.forEach(id => body.appendChild(hosted.get(id).container));

            if (!order.length) {
                activeId = null;
                renderTabs();
                renderExtras(null);
                hide();
                return;
            }
            activate(order.includes(activeId) ? activeId : order[0]);
        },

        // Route a selection into the dock. `meta.targetLayer` lets the click
        // that caused it also choose which tab answers -- clicking a
        // commentary marker in the body should raise the commentary, not
        // whatever tab happened to be open.
        select(addr, meta = {}) {
            lastAddr = addr;
            lastMeta = meta;
            hosted.forEach(entry => { entry.stale = true; });
            if (meta.targetLayer && hosted.has(meta.targetLayer)) {
                activate(meta.targetLayer);
                show();
                return;
            }
            if (root.hidden) return;   // stays stale until shown
            pushSelection(hosted.get(activeId));
        },

        activate,
        show,
        hide,
        toggle(on) { (on == null ? root.hidden : on) ? show() : hide(); },

        refresh() {
            const entry = hosted.get(activeId);
            if (entry && entry.instance && typeof entry.instance.refresh === 'function') {
                entry.instance.refresh();
            }
        },

        instance(id) {
            const entry = hosted.get(id);
            return entry ? entry.instance : null;
        },

        destroy() {
            hosted.forEach(entry => {
                if (entry.instance && typeof entry.instance.destroy === 'function') {
                    try { entry.instance.destroy(); } catch (err) { /* tearing down anyway */ }
                }
            });
            hosted.clear();
            order = [];
            activeId = null;
            root.innerHTML = '';
            root.classList.remove('mvc-dock');
        },
    };
}

function el(tag, className) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    return node;
}
